import React, { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import {
  Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Unlink,
  List, ListOrdered, Heading2, Heading3, Pilcrow, Palette, Undo2, Redo2,
} from "lucide-react";

const PRESET_COLORS = [
  { name: "Default", value: "" },
  { name: "Burgundy", value: "#6B1F2A" },
  { name: "Charcoal", value: "#1a1a1a" },
  { name: "Grey", value: "#666666" },
  { name: "Forest", value: "#2F5233" },
  { name: "Navy", value: "#1F3A5F" },
  { name: "Gold", value: "#A6802C" },
];

function ToolbarButton({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className={`p-2 rounded hover:bg-neutral-100 transition-colors ${active ? "bg-[#FAF3F4] text-[#6B1F2A]" : "text-neutral-700"}`}
    >
      {children}
    </button>
  );
}

function ColorPicker({ editor }) {
  const [open, setOpen] = useState(false);
  if (!editor) return null;
  const current = editor.getAttributes("textStyle").color || "";
  return (
    <div className="relative">
      <ToolbarButton
        active={!!current}
        onClick={() => setOpen(!open)}
        title="Text color"
      >
        <span className="inline-flex items-center gap-1">
          <Palette size={14} />
          <span className="w-3 h-3 rounded-sm border border-neutral-300" style={{ backgroundColor: current || "#1a1a1a" }} />
        </span>
      </ToolbarButton>
      {open && (
        <div className="absolute z-10 mt-1 bg-white border border-neutral-200 shadow-lg p-2 rounded grid grid-cols-7 gap-1 min-w-[200px]">
          {PRESET_COLORS.map(c => (
            <button
              key={c.value}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (c.value) editor.chain().focus().setColor(c.value).run();
                else editor.chain().focus().unsetColor().run();
                setOpen(false);
              }}
              title={c.name}
              className="w-7 h-7 rounded border border-neutral-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: c.value || "transparent", backgroundImage: !c.value ? "linear-gradient(135deg, transparent 45%, #aaa 45%, #aaa 55%, transparent 55%)" : undefined }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LinkButton({ editor }) {
  if (!editor) return null;
  const isActive = editor.isActive("link");

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (use /page-slug for internal pages, https://… for external)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <>
      <ToolbarButton active={isActive} onClick={setLink} title="Insert / edit link">
        <LinkIcon size={14} />
      </ToolbarButton>
      {isActive && (
        <ToolbarButton onClick={() => editor.chain().focus().unsetLink().run()} title="Remove link">
          <Unlink size={14} />
        </ToolbarButton>
      )}
    </>
  );
}

export default function RichTextEditor({ value, onChange, minHeight = 140 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-content focus:outline-none prose-body",
        style: `min-height:${minHeight}px;`,
      },
    },
  });

  // Keep editor in sync if external value changes (e.g., switching blocks)
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && value !== undefined) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-neutral-300 rounded-md bg-white" data-testid="rich-text-editor">
      <div className="flex items-center flex-wrap gap-0.5 px-2 py-1 border-b border-neutral-200 bg-neutral-50">
        <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Cmd/Ctrl+B)">
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Cmd/Ctrl+I)">
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline (Cmd/Ctrl+U)">
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <ColorPicker editor={editor} />
        <span className="w-px h-5 bg-neutral-300 mx-1" />
        <ToolbarButton active={editor.isActive("paragraph") && !editor.isActive("heading")} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragraph">
          <Pilcrow size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading">
          <Heading2 size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Subheading">
          <Heading3 size={14} />
        </ToolbarButton>
        <span className="w-px h-5 bg-neutral-300 mx-1" />
        <ToolbarButton active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list">
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
          <ListOrdered size={14} />
        </ToolbarButton>
        <span className="w-px h-5 bg-neutral-300 mx-1" />
        <LinkButton editor={editor} />
        <span className="w-px h-5 bg-neutral-300 mx-1" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
          <Undo2 size={14} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
          <Redo2 size={14} />
        </ToolbarButton>
      </div>
      <div className="px-4 py-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
